import { AutosaveStatusIcon } from '@src/components/common/AutosaveStatusIcon';
import { ArrowIcon } from '@src/components/common/icons';
import SplitButton from '@src/components/common/SplitButton';
import { MODAL_COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { InProgressSpinner } from '@src/components/transcript/inProgressChipStyles';
import CheckIcon from '@src/svgs/CheckIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import { EditPenIcon } from '@src/svgs/EditPenIcon';
import LinkIcon from '@src/svgs/LinkIcon';
import { MicStrokeIcon } from '@src/svgs/MicStrokeIcon';
import RefreshIcon from '@src/svgs/RefreshIcon';
import { ContentTab } from '@src/types.ts/general';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

/**
 * PLAT-11649 — which job the reviewer is doing, and so which layout the
 * workspace wears. `translation` is the three-panel layout (source, target,
 * changes side by side — correctness). `dubbing` is the translation beside a
 * large captioned player with both side panes collapsed to rails — delivery:
 * mouths, rhythm, stress; the original words matter less.
 */
export type ReviewMode = 'translation' | 'dubbing';

/**
 * What the Dubbing tab says about the reviewed language (PLAT-11706).
 * `dubbing` (PLAT-11741): its dub is generating right now — the state between
 * pressing Generate and the dub landing, which the tab has to show because
 * the modal that started the run may already be closed.
 */
export type ReviewDubStatus = 'dubbed' | 'not-dubbed' | 'dubbing';

/**
 * The pill's label and the tab's hover, per status. PLAT-12054 — the
 * invitation to open Dubbing is for the reviewer on the other tab; on the
 * Dubbing tab itself the hover states the fact and no more.
 */
function describeDubStatus(
  status: ReviewDubStatus,
  languageLabel: string,
  onDubbingTab: boolean,
): { label: string; hint: string } {
  switch (status) {
    case 'dubbed':
      return {
        label: 'Dubbed',
        hint: onDubbingTab
          ? `${languageLabel} has a dub.`
          : `${languageLabel} has a dub. Open Dubbing to review it.`,
      };
    case 'dubbing':
      // The verb in flight plus a single-character ellipsis (Loading &
      // Progress). The hover says the wait does not hold the review up.
      return {
        label: 'Dubbing…',
        hint: `Generating the ${languageLabel} dub. You can keep reviewing meanwhile.`,
      };
    case 'not-dubbed':
      return {
        label: 'Not dubbed',
        hint: onDubbingTab
          ? `No dub for ${languageLabel} yet.`
          : `No dub for ${languageLabel} yet. Open Dubbing to generate one.`,
      };
  }
}

/** PLAT-12054 — the Translation tab's hover, only from the other tab. */
function translationTabHint(languageLabel: string): string {
  return `Open Translation to review the ${languageLabel} text line by line.`;
}

/** Which affordance took the reviewer out of the review (analytics). */
export type ReviewExitMethod = 'back' | 'breadcrumb' | 'close' | 'escape';

/**
 * The review's ambient save state, shown by the shared cloud icon: `idle`
 * reads "Changes are saved automatically" — the review has no Save button, so
 * it says so where one would be looked for.
 */
export type ReviewSaveStatus = 'idle' | 'saving' | 'saved';

/**
 * `page`: the review is a place beside the app sidebar — the back arrow is the
 * one way out, no close X. `modal`: a dialog over another surface — the back
 * arrow AND the dialog's X.
 */
export type ReviewHeaderVariant = 'page' | 'modal';

// PLAT-11661 — the page's tabs, in the Content Studio tab strip's own style
// (`ContentView`'s `TabItem`: 14px, an icon, the gold underline on the rule):
// which JOB the reviewer is doing is the page's primary navigation, one clear
// step above the player's audio setting.
const REVIEW_MODE_TABS: {
  id: ReviewMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'translation',
    label: 'Translation',
    icon: <EditPenIcon size={14} />,
  },
  {
    id: 'dubbing',
    label: 'Dubbing',
    icon: <MicStrokeIcon size={14} />,
  },
];

const HEADER_TOOLTIP_ID = 'review-header-tooltip';

export interface TranslationReviewHeaderProps {
  variant: ReviewHeaderVariant;
  /** The reviewed language's display name ("Spanish (Mexico)"). */
  languageLabel: string;
  /**
   * The story under review; absent while it is still loading. Not a crumb of
   * its own — the row was taking too much space — but the hover on the crumb
   * before this review says which story that clip or tab belongs to.
   */
  storyTitle?: string;
  /**
   * The clip under review, on the clip-preview entry. Its presence is what
   * says which surface the reviewer came from: the crumb trail and the Back
   * label name the clip instead of the Transcript & Speakers tab.
   */
  clipTitle?: string;
  /**
   * What the clip entry's parent IS — a Reel is a clip record too, but the
   * product never calls it a clip (PLAT-11656 follow-up). Ignored without
   * `clipTitle`. Defaults to Clip.
   */
  clipKind?: 'Clip' | 'Reel';
  /**
   * PLAT-11991 — the story surface's tab name for this showcase (plain
   * "Transcript" without languages); the enum value when not given.
   */
  parentTabLabel?: string;
  saveStatus: ReviewSaveStatus;
  /** When the last persist for this language landed; drives the icon's tooltip. */
  lastSavedAt: Date | null;
  /**
   * Stacking level for the save icon's details popover. It portals to
   * `document.body`, so it must clear the workspace overlay — also body-level,
   * and above 1,000,000 — or the click opens it behind the panel (PLAT-11654).
   */
  savePopoverZIndex?: number;
  reviewMode: ReviewMode;
  onReviewModeChange: (next: ReviewMode) => void;
  /**
   * PLAT-11706 — whether the reviewed language has a dub, shown on the Dubbing
   * tab so the reviewer knows before opening it. Omit when dubbing is not
   * offered for this language (feature off, or nothing here can generate).
   * `dubbing` while its dub is generating (PLAT-11741).
   */
  dubStatus?: ReviewDubStatus;
  linkCopied: boolean;
  onCopyReviewLink: () => void;
  onRetranslateAll: () => void;
  onExit: (method: Exclude<ReviewExitMethod, 'escape'>) => void;
}

/**
 * The Translation Review's header, one row: WHERE the reviewer is, then the
 * review's own controls.
 *
 * Left: the Creator Studio back control (icon-only, its name on the tooltip)
 * and the crumb trail from the story down to this review — testers lost track
 * of being inside a story at all. Right: the Translation / Dubbing mode, the
 * review link, the shared save-state cloud ("Changes are saved automatically"
 * — testers feared closing would lose their work), and the dialog's X. The
 * transcript's own controls (Paragraph / Segment / Words, Split into Words)
 * live in the transcript pane's header, not here.
 */
export const TranslationReviewHeader: React.FC<
  TranslationReviewHeaderProps
> = ({
  variant,
  languageLabel,
  storyTitle,
  clipTitle,
  clipKind = 'Clip',
  saveStatus,
  lastSavedAt,
  savePopoverZIndex,
  reviewMode,
  onReviewModeChange,
  dubStatus,
  linkCopied,
  onCopyReviewLink,
  onRetranslateAll,
  onExit,
  parentTabLabel = ContentTab.transcript_speakers,
}) => {
  // The surface the reviewer came from, and returns to. The tab label is what
  // the host's tab bar shows, so the crumb reads exactly as the tab does.
  const parentLabel = clipTitle ?? parentTabLabel;
  const parentNoun = clipTitle ? clipKind : parentTabLabel;
  const backLabel = `Back to ${parentNoun}`;
  // The crumb's hover says where it goes AND which story that is — the story
  // used to be a crumb of its own, and the row took too much space for it.
  const parentHint = storyTitle
    ? `Go to ${parentNoun} from Story “${storyTitle}”`
    : `Go to ${parentNoun}`;

  return (
    <Header>
      <BackGroup>
        {/* The Creator Studio back control (BackToContentStudioButton): a
            boxed arrow, its name on the tooltip, a hairline after it. */}
        <BackButton
          type="button"
          aria-label={backLabel}
          data-testid="review-back"
          data-tooltip-id={HEADER_TOOLTIP_ID}
          data-tooltip-content={backLabel}
          onClick={() => onExit('back')}
        >
          &larr;
        </BackButton>
        <Separator />
        <Tooltip
          id={HEADER_TOOLTIP_ID}
          place="bottom"
          style={MODAL_COMPACT_TOOLTIP_STYLE}
        />
      </BackGroup>
      <Crumbs aria-label="You are here">
        {/* The crumb before this review: the clip, or the tab. A clip gets a
            type tag — the sidebar says "Content Studio", the trail says WHICH
            kind of thing inside it; the tab needs none, its name is the type. */}
        {clipTitle && <CrumbTag aria-hidden="true">{clipKind}</CrumbTag>}
        <CrumbButton
          type="button"
          data-testid="review-crumb-parent"
          data-tooltip-id={HEADER_TOOLTIP_ID}
          data-tooltip-content={parentHint}
          onClick={() => onExit('breadcrumb')}
        >
          {parentLabel}
        </CrumbButton>
        <CrumbSeparator aria-hidden="true">
          <ArrowIcon />
        </CrumbSeparator>
        <CurrentCrumb title={`Translation Review · ${languageLabel}`}>
          Translation Review <Dim>·</Dim> {languageLabel}
        </CurrentCrumb>
      </Crumbs>
      <ModeTabs
        role="tablist"
        aria-label="Review mode"
        data-testid="review-mode-tabs"
      >
        {REVIEW_MODE_TABS.map(tab => {
          // The dub status rides the Dubbing tab (PLAT-11706): a reviewer on
          // the Translation tab learns whether the language is dubbed without
          // opening the other mode, and the hover says what that mode offers.
          const selected = reviewMode === tab.id;
          // A run in flight shows its own progress bar on the Dubbing tab, so
          // the tab's spinner is only for the reviewer on the other tab.
          const status =
            tab.id === 'dubbing' && !(selected && dubStatus === 'dubbing')
              ? dubStatus
              : undefined;
          const described = status
            ? describeDubStatus(status, languageLabel, selected)
            : undefined;
          // PLAT-12054 — a tab's hover invites you over only from the other
          // tab: the Translation tab says what it offers while Dubbing is
          // open, and nothing while it is the open one.
          const statusHint =
            tab.id === 'translation'
              ? selected
                ? undefined
                : translationTabHint(languageLabel)
              : described?.hint;
          return (
            <ModeTab
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={reviewMode === tab.id}
              $selected={reviewMode === tab.id}
              data-testid={`review-mode-${tab.id}`}
              onClick={() => onReviewModeChange(tab.id)}
              data-tooltip-id={statusHint ? HEADER_TOOLTIP_ID : undefined}
              data-tooltip-content={statusHint}
            >
              {tab.icon}
              {tab.label}
              {status && described && (
                <ModeTabStatus
                  $status={status}
                  data-testid={`review-mode-${tab.id}-status`}
                  data-status={status}
                >
                  {/* PLAT-11741 — a run in flight takes the shared in-progress
                      ring where the dot was: the one thing on the page that
                      moves while the dub is made, so a closed modal does not
                      leave the reviewer guessing whether Generate took. */}
                  {status === 'dubbing' ? (
                    <StatusSpinner aria-hidden="true" />
                  ) : (
                    <StatusDot aria-hidden="true" />
                  )}
                  <StatusLabel>{described.label}</StatusLabel>
                </ModeTabStatus>
              )}
            </ModeTab>
          );
        })}
      </ModeTabs>
      <HeaderActions>
        {/* One control: the review link is the main segment, Retranslate All
            lives behind the chevron — a document-wide retranslate is better one
            deliberate click away than permanently on show. The link is the
            primary action of this page (PLAT-11514 originally made the header
            primary-less; handing the review to a translator is what a reviewer
            comes here to do, so it now carries the fill). */}
        <SplitButton
          variant="primary"
          // The link glyph already says "copy"; the word would repeat it.
          label={linkCopied ? 'Link Copied' : 'Review Link'}
          // Reserve the wider of the two labels so the copied-state swap
          // doesn't resize the control and shove its neighbours sideways.
          widestLabel="Link Copied"
          icon={
            linkCopied ? (
              <CheckIcon fillColor="currentColor" width="14" height="14" />
            ) : (
              <LinkIcon strokeColor="currentColor" width="16" />
            )
          }
          onClick={onCopyReviewLink}
          menuAriaLabel="More review actions"
          menuWidth="trigger"
          items={[
            {
              key: 'retranslate-all',
              label: 'Retranslate All',
              // Same glyph TranslationToggle uses for "regenerate this
              // translation" — one glyph, one meaning.
              icon: (
                <RefreshIcon
                  width="14"
                  height="14"
                  strokeColor="currentColor"
                />
              ),
              onSelect: onRetranslateAll,
            },
          ]}
        />
        {/* No Save button anywhere in the review, because every change
            persists at once — so the shared cloud says so where a Save button
            would be looked for. Status is ambient; failure is an event (a
            toast), never a parked red state. The slot holds its width while
            the icon stands down mid-save, so the X never shifts. */}
        <SaveSlot data-testid="review-save-status" data-status={saveStatus}>
          <AutosaveStatusIcon
            status={saveStatus}
            lastSavedAt={lastSavedAt}
            popoverZIndex={savePopoverZIndex}
            // At the header's right edge, inside a clipped surface: open
            // leftward, or the popover is cut off (and runs off the viewport).
            popoverAlign="end"
          />
        </SaveSlot>
        {/* A page has one way out (the back arrow); a dialog keeps its X too
            (Escape parity and the dialog convention). */}
        {variant === 'modal' && (
          <CloseButton
            type="button"
            aria-label="Close translation review"
            onClick={() => onExit('close')}
          >
            <CloseIcon />
          </CloseButton>
        )}
      </HeaderActions>
    </Header>
  );
};

const Header = styled.div`
  flex-shrink: 0;
  display: grid;
  // Back | crumbs (flexible, the parent crumb truncates) | the mode tabs,
  // centred | the review's controls. The two flexible columns keep the tabs at
  // the header's centre whatever the crumb or the actions measure.
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  min-height: 48px;
  gap: 12px;
  align-items: center;
  /* PLAT-11514 — the left inset matches the transcript's own text edge so the
     back control, the pane headers and the speaker labels share ONE vertical
     line. That edge is the Column's 12px padding plus the row text inset
     (AlignedBlocksView's ROW_TEXT_INSET, --spacing-sm). The right stays at
     16px for the close button. */
  padding: 0 16px 0 20px;
  border-bottom: 1px solid var(--border);
`;

// `ContentView`'s tab strip, byte for byte where it matters: 14px text, normal
// weight, a 14px icon at 0.6 opacity, and the gold underline sitting ON the
// header's rule (`bottom: -1px`). Stretched to the header's height so that
// underline lands there whatever the row measures. Right, beside the
// controls, so the crumb keeps its room.
const ModeTabs = styled.nav`
  align-self: stretch;
  display: flex;
  align-items: stretch;
  gap: var(--spacing-lg);
  margin-right: var(--spacing-md);
`;

/* The tab's status: quieter than the label, the dot carrying the state —
   green for a dub the reviewer can hear, gold for the creative step still to
   take. Excluded from the tab's own svg opacity rule (it is not the icon). */
const StatusDot = styled.span`
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

/* The dub generating (PLAT-11741): the shared in-progress ring
   (`DubRunChip`'s), sized down to the dot's row — the pill's text is xs and a
   12px ring reads as a second icon beside the tab's own. */
const StatusSpinner = styled(InProgressSpinner)`
  width: 10px;
  height: 10px;
  border-width: 1.5px;
`;

/* Level with the tab label in every state. PLAT-11895 raised it like a
   superscript so it would read as an annotation; in use the raise read as a
   misaligned pill, so it is centred again (PLAT-12031). */
const ModeTabStatus = styled.span<{ $status: ReviewDubStatus }>`
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 5px;
  box-sizing: border-box;
  height: 20px;
  margin-left: 2px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: 1;
  color: var(--text-muted);
  white-space: nowrap;

  ${StatusDot} {
    color: ${props =>
      props.$status === 'dubbed' ? 'var(--primary)' : 'var(--accent)'};
  }
`;

/* The font's ascent is taller than its descent, so a line box centred in the
   pill still sits the letters low. Trimming the box to cap height and
   baseline makes the pill centre the glyphs themselves, level with the dot. */
const StatusLabel = styled.span`
  display: block;
  text-box: trim-both cap alphabetic;
`;

const ModeTab = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  transition: color var(--transition-fast);

  svg {
    flex-shrink: 0;
    opacity: 0.6;
  }

  &:hover {
    color: var(--foreground);

    svg {
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: -2px;
  }

  ${props =>
    props.$selected &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium);

      svg {
        opacity: 1;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;

const BackGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

// The Creator Studio back control, byte for byte (`BackToContentStudioButton`):
// the ← glyph on a transparent box that fills only on hover — a resting fill
// read as a permanently hovered button.
const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 25px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-input);
  font-size: 16px;
  transition: all var(--transition-fast) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
`;

// Matches the timeline controls' VerticalSeparator, as in Creator Studio.
const Separator = styled.div`
  width: 1px;
  height: 20px;
  background-color: var(--border);
`;

const Crumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: var(--text-sm);
`;

// The ancestor crumb IS a way back — leaving the review is how you return to
// the surface it names. Muted at rest, gold on hover like the Creator Studio
// breadcrumbs; it is what gives way (ellipsis) when the row is narrow — the
// current step never does.
const CrumbButton = styled.button`
  min-width: 0;
  padding: 2px 4px;
  margin: 0 -4px;
  border: 0;
  border-radius: var(--radius-sm, 6px);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    color: var(--accent);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
`;

// A neutral type tag (never gold — gold is a creative control): says what kind
// of thing the crumb is, so "Content Studio" in the sidebar and the clip name
// in the trail read as one path.
const CrumbTag = styled.span`
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface-elevated);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  line-height: 1.4;
`;

const CrumbSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

// The review itself: the trail's last step, and the surface's heading.
const CurrentCrumb = styled.h2`
  margin: 0;
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;
`;

const Dim = styled.span`
  color: var(--text-muted);
`;

// PLAT-10258 (Atom 4) — right-side header cluster: the review's own controls.
const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
`;

// The cloud icon stands down while saving (one activity indicator at a time,
// by its own contract); the slot keeps its 28px so nothing beside it moves.
const SaveSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;
